import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { ScanLine, Loader2, CheckCircle, XCircle, Clock, FileText, ExternalLink } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

export default function AdminScanApprovals() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [acting, setActing] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/admin/scan-sessions?status=${tab}&limit=50`);
      setRows(data.sessions || []);
      setPendingCount(data.pending_count || 0);
    } catch { setRows([]); } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

  const approve = async (id) => {
    setActing(id);
    try { await API.post(`/admin/scan-sessions/${id}/approve`); await load(); }
    catch (e) { alert(e.response?.data?.detail || "Approve failed"); }
    finally { setActing(""); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection (optional) — the customer will see this and can re-scan:");
    if (reason === null) return;
    setActing(id);
    try { await API.post(`/admin/scan-sessions/${id}/reject`, { reason }); await load(); }
    catch (e) { alert(e.response?.data?.detail || "Reject failed"); }
    finally { setActing(""); }
  };

  return (
    <div className="space-y-6" data-testid="admin-scan-approvals">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#E0F4F5] flex items-center justify-center"><ScanLine className="w-5 h-5 text-[#008A99]" /></div>
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Scan Approvals</h2>
          <p className="text-xs text-slate-500">Review customer QR-scan batches + invoices. Points are credited only on approval.</p>
        </div>
      </div>

      <div className="flex gap-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${tab === t.key ? "bg-[#008A99] text-white" : "bg-white border text-slate-600 hover:border-[#008A99]"}`}
            data-testid={`scan-tab-${t.key}`}>
            {t.label}{t.key === "pending" && pendingCount > 0 ? ` (${pendingCount})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#008A99] mx-auto" /></div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-slate-400 text-sm bg-white rounded-2xl border" data-testid="scan-approvals-empty">
          <ScanLine className="w-10 h-10 mx-auto mb-2 text-slate-200" />
          No {tab} scan submissions.
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((s) => (
            <div key={s.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid={`scan-session-${s.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-bold text-slate-800">{s.user_name || "—"}</p>
                  <p className="text-xs text-slate-400">{s.user_mobile || "—"}{s.registry_id ? ` · Reg ${s.registry_id}` : ""}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Submitted {fmtDate(s.submitted_at)}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-black text-[#008A99]">{s.estimated_points || 0} pts</p>
                  <p className="text-[11px] text-slate-400">{(s.items || []).length} reagent{(s.items || []).length === 1 ? "" : "s"}</p>
                  {s.status === "approved" && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 mt-1"><CheckCircle className="w-3 h-3" />Approved</span>}
                  {s.status === "rejected" && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 mt-1"><XCircle className="w-3 h-3" />Rejected</span>}
                  {s.status === "pending" && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 mt-1"><Clock className="w-3 h-3" />Pending</span>}
                </div>
              </div>

              <div className="mt-3 grid gap-2">
                {(s.items || []).map((it, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 border border-slate-100" data-testid={`scan-session-item-${s.id}-${i}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{it.product_name || it.product_code}</p>
                      <p className="text-[11px] text-slate-400 font-mono truncate">{it.qr_string}</p>
                    </div>
                    <span className="text-xs font-bold text-[#008A99] flex-shrink-0 ml-2">+{it.points_earned || 0}</span>
                  </div>
                ))}
              </div>

              <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                {s.invoice_url ? (
                  <a href={`${BACKEND}${s.invoice_url}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#008A99] hover:underline" data-testid={`view-invoice-${s.id}`}>
                    <FileText className="w-4 h-4" /> View invoice <ExternalLink className="w-3 h-3" />
                  </a>
                ) : <span className="text-xs text-slate-400">No invoice</span>}

                {s.status === "pending" && (
                  <div className="flex gap-2">
                    <button onClick={() => reject(s.id)} disabled={acting === s.id}
                      className="px-4 py-2 rounded-xl border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50 disabled:opacity-50" data-testid={`reject-session-${s.id}`}>
                      Reject
                    </button>
                    <button onClick={() => approve(s.id)} disabled={acting === s.id}
                      className="px-5 py-2 rounded-xl bg-[#008A99] text-white text-sm font-bold hover:bg-[#006670] disabled:opacity-50 flex items-center gap-1.5" data-testid={`approve-session-${s.id}`}>
                      {acting === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      Approve & Credit
                    </button>
                  </div>
                )}
                {s.status === "rejected" && s.admin_notes && (
                  <p className="text-xs text-red-500">Reason: {s.admin_notes}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
